import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { 
  Form, 
  Row, 
  Col, 
  Button, 
  Table, 
  Space, 
  Popconfirm, 
  message,
  Card,
  Typography,
  Tag,
  Tooltip,
  Statistic,
  InputNumber
} from 'antd';
import { 
  DeleteOutlined, 
  HeartOutlined, 
  InfoCircleOutlined
} from '@ant-design/icons';
import moment from 'moment';
import { 
  SectionTitle,
  FormSection 
} from '../styled';

const { Text } = Typography;

// Added hideTitle prop to allow embedding inside an existing Vital Signs section
// Added hideInlineAddButton to suppress the internal left-side button when embedding
const MultipleReadingsTab = forwardRef(({ form, patient, currentVisit, initialReadings = [], onNewReadingAdded, onReadingsUpdated, onAddFormVisibilityChange, hideTitle = false, hideInlineAddButton = false, allowHistoricalReadings = false }, ref) => {
  // Organize readings by visit
  const [currentVisitReadings, setCurrentVisitReadings] = useState([]);
  const [systolicBP, setSystolicBP] = useState();
  const [diastolicBP, setDiastolicBP] = useState();
  const [heartRate, setHeartRate] = useState();
  const [adding, setAdding] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isHistoricalDay, setIsHistoricalDay] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false); // Track if initial load complete
  const isPastVisitLocked = isHistoricalDay && !allowHistoricalReadings;

  // Expose imperative controls to parent (e.g., right-side header button)
  useImperativeHandle(ref, () => ({
    toggleAddForm: () => setShowAddForm(s => !s),
    openAddForm: () => setShowAddForm(true),
    closeAddForm: () => setShowAddForm(false)
  }), []);

  // Notify parent when the add form visibility changes
  useEffect(() => {
    if (typeof onAddFormVisibilityChange === 'function') {
      try { onAddFormVisibilityChange(showAddForm); } catch(e) { /* noop */ }
    }
  }, [showAddForm, onAddFormVisibilityChange]);

  // Determine whether current visit's date is today (local) for allowing new readings
  useEffect(() => {
    const visitId = currentVisit?.visitId || patient?.visitId;
    const all = form.getFieldValue('medicalReadings') || initialReadings || [];
    const visit = all.find(v => v.visitId === visitId);
    const visitDate = visit?.visitDate ? moment(visit.visitDate) : null;
    if (visitDate) {
      setIsHistoricalDay(!visitDate.isSame(moment(), 'day'));
    } else {
      // If no explicit visitDate yet (brand new visit) treat as today
      setIsHistoricalDay(false);
    }
  }, [currentVisit, patient, form, initialReadings]);

  // Initialize data from form's medicalReadings or initialReadings
  useEffect(() => {
    // Always check form values first (updated in real-time)
    const formReadings = form.getFieldValue('medicalReadings');
    const readingsToUse = formReadings && formReadings.length > 0 ? formReadings : initialReadings;
    
    if (!readingsToUse || readingsToUse.length === 0) {
      setCurrentVisitReadings([]);
      return;
    }
    
    // Organize readings by visit
    const organized = {};
    readingsToUse.forEach(visitData => {
      const visitId = visitData.visitId || 'legacy';
      organized[visitId] = {
        ...visitData,
        visitDate: visitData.visitDate || new Date(),
        eventName: visitData.eventName || 'Unknown Event',
        location: visitData.location || 'Unknown Location',
        vitalSigns: visitData.vitalSigns || []
      };
    });
    
    // Get current visit ID
    const currentVisitId = currentVisit?.visitId || patient?.visitId || 'current';
    const currentVisitData = organized[currentVisitId];
    
    // Normalize existing readings with IDs
    const normalized = (currentVisitData?.vitalSigns || []).map((r, idx) => ({
      id: r.id || r._id || `${currentVisitId}_${r.timestamp || idx}_${idx}`,
      readingNumber: r.readingNumber || idx + 1,
      ...r
    }));
    setCurrentVisitReadings(normalized);
    // Only notify parent of changes after initialization complete (not during initial load)
    if (isInitialized && typeof onReadingsUpdated === 'function') {
      try { onReadingsUpdated(normalized); } catch(e) { /* noop */ }
    }
    // Mark as initialized after first load
    if (!isInitialized) {
      setIsInitialized(true);
    }
  }, [initialReadings, currentVisit, patient, form, isInitialized]);

  const calculateAverage = (readings, field) => {
    if (!readings || readings.length === 0) return null;
    const sum = readings.reduce((acc, reading) => acc + (reading[field] || 0), 0);
    return Math.round(sum / readings.length);
  };

  const updateFormReadings = (updatedReadings) => {
    const visitId = currentVisit?.visitId || patient?.visitId || 'current';
    // CRITICAL: Create a new array copy to ensure Ant Design Form detects the change
    // Mutating the original array in-place caused form.setFieldsValue to skip updates
    // because it saw the same reference, leading to blood pressure readings being lost
    const medicalReadings = [...(form.getFieldValue('medicalReadings') || [])];
    const idx = medicalReadings.findIndex(v => v.visitId === visitId);
    if (idx >= 0) {
      // Create a new object for the visit entry to ensure proper change detection
      medicalReadings[idx] = {
        ...medicalReadings[idx],
        vitalSigns: updatedReadings.map((r, i) => ({
          systolicBP: r.systolicBP,
          diastolicBP: r.diastolicBP,
          heartRate: r.heartRate,
          timestamp: r.timestamp,
          position: r.position || 'sitting',
          readingNumber: i + 1,
          id: r.id
        }))
      };
    } else {
      medicalReadings.push({
        visitId,
        visitDate: new Date(),
        eventId: currentVisit?.eventId,
        eventName: currentVisit?.eventName || patient?.eventInfo?.eventTitle || 'Current Screening',
        location: currentVisit?.location || patient?.eventInfo?.eventLocation || 'Current Location',
        vitalSigns: updatedReadings.map((r, i) => ({
          systolicBP: r.systolicBP,
          diastolicBP: r.diastolicBP,
          heartRate: r.heartRate,
          timestamp: r.timestamp,
          position: r.position || 'sitting',
          readingNumber: i + 1,
          id: r.id
        }))
      });
    }
    form.setFieldsValue({ medicalReadings });
  };

  const removeReading = (id) => {
    const updated = currentVisitReadings.filter(reading => reading.id !== id);
    setCurrentVisitReadings(updated);
    updateFormReadings(updated);
    message.success('Reading removed');
    if (typeof onReadingsUpdated === 'function') {
      try { onReadingsUpdated(updated); } catch(e) { /* noop */ }
    }
  };

  const addReading = () => {
    if (isPastVisitLocked) {
      message.warning('This visit occurred on a previous date. Start a new visit to add today\'s readings.');
      return;
    }
    if (!systolicBP || !diastolicBP || !heartRate) {
      message.warning('Enter Systolic, Diastolic and Heart Rate');
      return;
    }
    if (systolicBP < 50 || systolicBP > 300 || diastolicBP < 30 || diastolicBP > 200) {
      message.warning('Values out of expected range');
      return;
    }
    setAdding(true);
    try {

      const newReading = {
        id: `reading_${Date.now()}_${Math.random().toString(36).slice(2,7)}`,
        systolicBP: Number(systolicBP),
        diastolicBP: Number(diastolicBP),
        heartRate: Number(heartRate),
        timestamp: new Date(),
        position: 'sitting',
        readingNumber: currentVisitReadings.length + 1
      };
      const updated = [...currentVisitReadings, newReading];
      setCurrentVisitReadings(updated);
      updateFormReadings(updated);

      // --- Fix: Also mirror the latest reading into the top-level vital sign fields so
      // completion fallback logic can still persist at least one reading if the
      // medicalReadings array is lost (e.g., component unmounted before submit).
      try {
        form.setFieldsValue({
          systolicBP: newReading.systolicBP,
          diastolicBP: newReading.diastolicBP,
          heartRate: newReading.heartRate
        });
      } catch(e) { /* silent */ }
      setSystolicBP(undefined);
      setDiastolicBP(undefined);
      setHeartRate(undefined);
      message.success('Reading added');
      if (typeof onNewReadingAdded === 'function') {
        try { onNewReadingAdded(newReading); } catch(e) { /* silent */ }
      }
      if (typeof onReadingsUpdated === 'function') {
        try { onReadingsUpdated(updated); } catch(e) { /* silent */ }
      }
    } finally {
      setAdding(false);
    }
  };

  const getBPCategory = (systolic, diastolic) => {
    if (systolic >= 180 || diastolic >= 110) {
      return { text: 'Hypertensive Crisis', color: '#f5222d' };
    } else if (systolic >= 140 || diastolic >= 90) {
      return { text: 'Stage 2 Hypertension', color: '#fa541c' };
    } else if (systolic >= 130 || diastolic >= 80) {
      return { text: 'Stage 1 Hypertension', color: '#fa8c16' };
    } else if (systolic >= 120) {
      return { text: 'Elevated', color: '#faad14' };
    } else {
      return { text: 'Normal', color: '#52c41a' };
    }
  };

  const getHRCategory = (heartRate) => {
    if (heartRate < 60) {
      return { text: 'Slow', color: '#1890ff' };
    } else if (heartRate > 100) {
      return { text: 'Fast', color: '#fa541c' };
    } else {
      return { text: 'Normal', color: '#52c41a' };
    }
  };

  const currentVisitColumns = [
    {
      title: 'Date',
      dataIndex: 'timestamp',
      key: 'date',
      render: (timestamp) => (
        <Text>{moment(timestamp).format('MM/DD/YYYY')}</Text>
      ),
      width: 100,
    },
    {
      title: 'Time',
      dataIndex: 'timestamp',
      key: 'time',
      render: (timestamp) => (
        <Text>{moment(timestamp).format('h:mm:ss A')}</Text>
      ),
      width: 110,
    },
    {
      title: 'Blood Pressure',
      key: 'bloodPressure',
      render: (record) => {
        const category = getBPCategory(record.systolicBP, record.diastolicBP);
        return (
          <Space size="small">
            <Text>{record.systolicBP}/{record.diastolicBP}</Text>
            <Tag color={category.color} style={{ fontSize: '12px' }}>
              {category.text}
            </Tag>
          </Space>
        );
      },
      width: 220,
    },
    {
      title: 'Heart Rate',
      key: 'heartRate',
      render: (record) => {
        const category = getHRCategory(record.heartRate);
        return (
          <Space size="small">
            <Text>{record.heartRate} bpm</Text>
            <Tag color={category.color} style={{ fontSize: '12px' }}>
              {category.text}
            </Tag>
          </Space>
        );
      },
      width: 180,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record) => (
        <Popconfirm
          title="Remove this reading?"
          description="This action cannot be undone."
          onConfirm={() => removeReading(record.id)}
          okText="Remove"
          cancelText="Cancel"
          okButtonProps={{ danger: true }}
        >
          <Button 
            type="text" 
            danger 
            icon={<DeleteOutlined />}
            size="small"
          />
        </Popconfirm>
      ),
      width: 80,
    },
  ];


  const getCurrentVisitStats = () => {
    if (currentVisitReadings.length === 0) return null;
    
    return {
      count: currentVisitReadings.length,
      avgSystolic: calculateAverage(currentVisitReadings, 'systolicBP'),
      avgDiastolic: calculateAverage(currentVisitReadings, 'diastolicBP'),
      avgHeartRate: calculateAverage(currentVisitReadings, 'heartRate'),
      latest: currentVisitReadings[currentVisitReadings.length - 1]
    };
  };

  const stats = getCurrentVisitStats();

  return (
    <>
      <style>
        {`
        .ghost-primary-visible.ant-btn-ghost {
          color: #1677ff !important;
          border-color: #1677ff !important;
          background: transparent !important;
        }
        .ghost-primary-visible.ant-btn-ghost:hover,
        .ghost-primary-visible.ant-btn-ghost:focus {
          color: #1677ff !important;
          border-color: #1677ff !important;
          background: rgba(22,119,255,0.06) !important;
        }
        `}
      </style>
      <FormSection style={hideTitle ? { paddingTop: 0 } : undefined}>
        {/* Section Title with Current Visit Info */}
        {!hideTitle && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 24,
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
            <SectionTitle style={{ margin: 0 }}>
              <HeartOutlined />
              Vital Signs
              <Tooltip title="Capture multiple BP & heart rate readings for this visit">
                <InfoCircleOutlined style={{ marginLeft: 8, fontSize: '14px', color: '#8c8c8c' }} />
              </Tooltip>
            </SectionTitle>
            <Text type="secondary" style={{ fontSize:12 }}>
              {currentVisit?.eventName || patient?.eventInfo?.eventTitle || 'Current Screening'} • {moment().format('MM/DD/YYYY')}
            </Text>
          </div>
          <Space>
            {isPastVisitLocked && (
              <Tooltip title="Cannot add readings to a past date. Start a new visit for today.">
                <Button disabled type="default">Add BP Reading</Button>
              </Tooltip>
            )}
            {!isPastVisitLocked && (
              <>
                {showAddForm && (
                  <Button ghost className="ghost-primary-visible" onClick={() => setShowAddForm(false)}>
                    Cancel
                  </Button>
                )}
                <Button type="primary" ghost className="ghost-primary-visible" onClick={() => setShowAddForm(s => !s)}>
                  {showAddForm ? 'Add Reading' : 'Add BP Reading'}
                </Button>
              </>
            )}
          </Space>
        </div>
        )}
        {hideTitle && !hideInlineAddButton && !isPastVisitLocked && (
          <div style={{ marginBottom: 16 }}>
            <Button type="primary" ghost className="ghost-primary-visible" onClick={() => setShowAddForm(s => !s)} size="small">
              {showAddForm ? 'Cancel' : 'Add BP Reading'}
            </Button>
          </div>
        )}

        {/* Current Visit Statistics */}
        {stats && (
          <Card size="small" style={{ marginBottom: 16 }}>
            <Row gutter={[16, 12]}>
              <Col xs={12} sm={6}>
                <Statistic
                  title="Readings Today"
                  value={stats.count}
                  prefix={<HeartOutlined />}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="Avg Systolic"
                  value={stats.avgSystolic}
                  suffix="mmHg"
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="Avg Diastolic"
                  value={stats.avgDiastolic}
                  suffix="mmHg"
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="Avg Heart Rate"
                  value={stats.avgHeartRate}
                  suffix="bpm"
                />
              </Col>
            </Row>
          </Card>
        )}

        {showAddForm && !isPastVisitLocked && (
          <Card size="small" style={{ marginBottom: 16, background: '#fafafa' }}>
            <Row gutter={[16, 12]} align="middle" wrap>
              <Col xs={24} sm={8}>
                <Text strong>Systolic (mmHg)</Text>
                <InputNumber 
                  value={systolicBP}
                  onChange={setSystolicBP}
                  placeholder="120"
                  min={50}
                  max={300}
                  style={{ width: '100%', marginTop: 4 }}
                  autoFocus
                />
              </Col>
              <Col xs={24} sm={8}>
                <Text strong>Diastolic (mmHg)</Text>
                <InputNumber 
                  value={diastolicBP}
                  onChange={setDiastolicBP}
                  placeholder="80"
                  min={30}
                  max={200}
                  style={{ width: '100%', marginTop: 4 }}
                />
              </Col>
              <Col xs={24} sm={8}>
                <Text strong>Heart Rate (bpm)</Text>
                <InputNumber 
                  value={heartRate}
                  onChange={setHeartRate}
                  placeholder="72"
                  min={30}
                  max={250}
                  style={{ width: '100%', marginTop: 4 }}
                />
              </Col>
              <Col xs={24}>
                <Button 
                  type="primary" 
                  onClick={() => { addReading(); if (systolicBP && diastolicBP && heartRate) setShowAddForm(false); }} 
                  loading={adding}
                  block
                  style={{ maxWidth: 200 }}
                >
                  Add Reading
                </Button>
              </Col>
            </Row>
            <div style={{ marginTop: 8 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {isPastVisitLocked ? 'Readings cannot be added to a past visit date.' : 'Enter the values and click Add Reading. You can add multiple readings per visit.'}
              </Text>
            </div>
          </Card>
        )}

        {/* Current Visit Readings */}
        <div style={{ marginBottom: 24 }}>
          <Table 
            columns={currentVisitColumns}
            dataSource={currentVisitReadings}
            rowKey="id"
            pagination={false}
            size="middle"
            locale={{
              emptyText: 'No readings yet. Click Add BP Reading to start.'
            }}
            bordered
            scroll={{ x: 'max-content' }}
            style={{
              backgroundColor: '#fff',
              boxShadow: '0 1px 2px rgba(0,0,0,0.03)'
            }}
          />
        </div>

        {/* Previous Visits summary removed; detailed history now lives in Visit History tab */}

        {/* Hidden form field to store readings data */}
        <Form.Item name="medicalReadings" style={{ display: 'none' }}>
          <input type="hidden" />
        </Form.Item>
      </FormSection>
    </>
  );
});
MultipleReadingsTab.displayName = 'MultipleReadingsTab';

export default MultipleReadingsTab;
